<script setup>
import { computed, onMounted } from 'vue';
import HighlightsSection from '@/components/configuratorSections/HighlightsSection';
import PowertrainSectionV2 from '@/components/configuratorSections/PowertrainSectionV2';
import ExteriorSection from '@/components/configuratorSections/ExteriorSection';
import InteriorSectionV2 from '@/components/configuratorSections/InteriorSectionV2';
import SafetySectionGroupV2 from '@/components/configuratorSections/SafetySectionGroupV2';
import ChassisSectionV2 from '@/components/configuratorSections/ChassisSectionV2';
import ConnectedServicesSection from '@/components/configuratorSections/ConnectedServicesSection';
import RepairMaintenanceSection from '@/components/configuratorSections/RepairMaintenanceSection';

import { useMainStore } from '@/store/index.js';

const store = useMainStore();

const staticImagesBaseUrl = process.env.VUE_APP_DS_STATIC_IMAGES_BASE_URL;

const props = defineProps({
  title: {
    type: String,
    default: '',
  },
  offerTemplate: {
    type: String,
    default: 'CH_STANDARD_STOCK',
    required: true,
  },
  specType: {
    type: String,
    default: '',
    required: true,
  },
  isFrameAgreement: {
    type: Boolean,
    default: false,
    required: true,
  },
});

const emit = defineEmits(['select']);

onMounted(() => {
  store.sections = [
    { id: 'highlights', name: 'highlightsHeaderTitle' },
    { id: 'powertrain', name: 'drivelineHeaderTitle' },
    { id: 'interior', name: 'interiorHeaderTitle' },
    { id: 'safety', name: 'interiorHeaderTitle' },
    ...(options.value.exterior ? [{ id: 'exterior', name: 'exteriorHeaderTitle' }] : []),
    ...(options.value.chassis ? [{ id: 'chassis', name: 'chassisHeaderTitle' }] : []),
    { id: 'connectedServices', name: 'connectedServicesHeaderTitle' },
    { id: 'repairMaintenance', name: 'repairMaintenanceHeaderTitle' },
  ];
});

const options = computed(() => {
  return store.options;
});

const images = computed(() => {
  return store.images;
});

const interiorImages = computed(() => {
  return { interior: images.value.interior, dashboard: images.value.dashboard };
});

const isBev = computed(() => {
  return props.offerTemplate.endsWith('_BEV');
});

const isTractor = computed(() => {
  return props.offerTemplate.endsWith('STANDARD_TRACTOR');
});

const isStock = computed(() => {
  return props.offerTemplate.endsWith('STANDARD_STOCK');
});

const imageChassisSection = computed(() => {
  return (store.sectionMetadata?.chassis?.descriptionImage) ? `${staticImagesBaseUrl}/${store.sectionMetadata?.chassis?.descriptionImage}` : null;
});

const chassisHighlights = computed(() => {
  return store.misc?.chassisHighlights;
});
</script>
<template>
  <HighlightsSection
    v-if="options.highlights"
    :items="options.highlights"
  />
  <PowertrainSectionV2
    :items="options.driveline"
    @select="emit('select', $event, 'driveline')"
  />
  <InteriorSectionV2
    v-if="options.interior"
    :items="options.interior"
    :images="interiorImages"
    @select="emit('select', $event, 'interior')"
  />
  <SafetySectionGroupV2
    :items="options.safety"
    @select="emit('select', $event, 'safety')"
  />
  <ExteriorSection
    v-if="options.exterior || options.cabColour"
    :cab-colour="options.cabColour"
    :exterior="options.exterior"
    :images="images"
    :show-cab-colour-information="true"
    @select="emit('select', $event, 'exterior')"
  />
  <ChassisSectionV2
    v-if="options.chassis"
    :items="options.chassis"
    :images="{ side: imageChassisSection, isSide: images?.side }"
    :offer-template="offerTemplate"
    :spec-type="specType"
    :is-frame-agreement="isFrameAgreement"
    :is-bev="isBev"
    :is-tractor="isTractor || isStock"
    :chassis-description-key="chassisHighlights"
    :show-dimensions-subheader="false"
    class="chasis-section"
    @select="emit('select', $event, 'chassis')"
  />
  <ConnectedServicesSection
    :items="options.connectedServices"
    @select="emit('select', $event, 'connectedServices')"
  />
  <RepairMaintenanceSection
    :items="options.repairMaintenance"
    @select="emit('select', $event, 'repairMaintenance')"
  />
</template>


<style scoped>
.chasis-section >>> .configurator-section__image > img {
  max-width: 70%;
}

@media only screen and (max-width: 1294px) {
  .chasis-section >>> .configurator-section__image > img {
    max-width: 80%;
  }
}

@media only screen and (max-width: 1000px) {
  .chasis-section >>> .configurator-section__image > img {
    max-width: 90%;
  }
}

@media only screen and (max-width: 600px) {
  .chasis-section >>> .configurator-section__image > img {
    max-width: 120%;
  }
}
</style>
